/**
 * Reward Composable
 * 管理獎勵相關功能
 * 原始來源: cuora/src/machine/reward.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/rewards/reward.js)
 * - 業務邏輯：保留在 composable 中
 */

import { storeToRefs } from 'pinia'
import { nextTick, onBeforeUnmount, getCurrentInstance } from 'vue'

import { activityApi, paymentApi } from '@/api'
import { useGameRoomDefault } from '@/composables/game/useGameRoomDefault'
import { useMainStore } from '@/stores/main'
import { useRewardStore } from '@/stores/rewards/reward'
import { identityKey, isRewardClaimAllowed } from '@/utils/identityGuard'
import { isRequestErrorAlreadySurfaced } from '@/utils/requestError'

// 領獎後要開的 Inbox 頁籤，依獎勵類別決定（TI-15786）。
// 後端把獎勵切成 CASH,FREE,PRIZE（REWARD 頁籤）與 POINTS（DIAMOND 頁籤）兩組查詢，這裡與之對齊。
const INBOX_TAB_TYPE_BY_REWARD_TYPE = {
  POINTS: 'DIAMOND',
  GAME: 'FREE_GAME',
  CASH: 'REWARD',
  FREE: 'REWARD',
  PRIZE: 'REWARD',
}

const FREE_REWARD_RETURN_DELAY_MS = 1000

export const useReward = () => {
  const nuxtApp = useNuxtApp()
  const { $request, $i18n } = nuxtApp
  const { appSecret } = useRuntimeConfig().public
  const { isLoggedIn, token } = useAuthToken()
  const mainStore = useMainStore()
  const { setLoading, setModal, setModalParams } = mainStore
  const { resetToDefaultRoom } = useGameRoomDefault()

  const store = useRewardStore()
  const {
    otherRewardList,
    pointsRewardList,
    rewardList,
    activityNo,
    receiveNo,
    withdrawRule,
    rewardType,
    isPlaying,
    callback,
    orderColumn,
    lang,
    receiving,
    pending,
    getPending,
  } = storeToRefs(store)

  // Initialize lang from i18n
  if ($i18n?.locale?.value && !lang.value) {
    store.setLang($i18n.locale.value)
  }

  // SSR-safe: 使用 nuxtApp context 存儲 timer，避免記憶體洩漏
  if (!nuxtApp._rewardContext) {
    nuxtApp._rewardContext = {
      refreshTimer: null,
      // SSR cleanup 方法
      cleanup: () => {
        const ctx = nuxtApp._rewardContext
        if (ctx?.refreshTimer) {
          clearInterval(ctx.refreshTimer)
          ctx.refreshTimer = null
        }
      },
    }
  }
  const ctx = nuxtApp._rewardContext

  const fetchRewardList = async () => {
    if (!isLoggedIn.value) {
      return
    }

    // 章取自「請求發出當下」而非回應回來時：飛行中換身分的那批回應要帶著舊身分的章寫入（BR-5）
    const ownerKey = identityKey(token.value)

    try {
      // 並行載入三種獎勵列表
      const [otherRewardRes, pointsRewardRes, rewardRes] = await Promise.all([
        $request.send(
          activityApi.getRewardList({
            rewardType: 'CASH,FREE,PRIZE',
            count: 30,
            appSecret,
            orderColumn: orderColumn.value,
            lang: lang.value,
          })
        ),
        $request.send(
          activityApi.getRewardList({
            rewardType: 'POINTS',
            count: 30,
            appSecret,
            lang: lang.value,
          })
        ),
        $request.send(
          activityApi.getRewardList({
            count: 30,
            appSecret,
            lang: lang.value,
          })
        ),
      ])

      store.setOtherRewardList(otherRewardRes.data || [], ownerKey)
      store.setPointsRewardList(pointsRewardRes.data || [], ownerKey)
      store.setRewardList(rewardRes.data || [], ownerKey)

      return {
        otherRewardList: otherRewardList.value,
        pointsRewardList: pointsRewardList.value,
        rewardList: rewardList.value,
      }
    } catch (error) {
      console.error('[useReward] fetchRewardList error:', error)
      throw error
    }
  }

  const fetchOtherRewardList = async (newOrderColumn = 'createTime') => {
    store.setOrderColumn(newOrderColumn)

    // 同 fetchRewardList：章取自「請求發出當下」的身分（TI-15612）
    const ownerKey = identityKey(token.value)

    try {
      const response = await $request.send(
        activityApi.getRewardList({
          rewardType: 'CASH,FREE,PRIZE',
          count: 30,
          appSecret,
          orderColumn: newOrderColumn,
          lang: lang.value,
        })
      )

      const data = response.data || response
      store.setOtherRewardList(data, ownerKey)

      return otherRewardList.value
    } catch (error) {
      console.error('[useReward] fetchOtherRewardList error:', error)
      throw error
    }
  }

  const withdrawData = ref({})
  const fetchWithdrawRulePreview = async ({ activityNo, receiveNo }) => {
    setLoading({ inbox: true })
    store.setActivityNo(activityNo)
    store.setReceiveNo(receiveNo)

    try {
      const response = await $request.send(
        paymentApi.getWithdrawRulePreview({
          applyType: 'activity',
          applyNo: receiveNo,
          amount: null,
        })
      )

      // 確保 response.data 不存在時也有預設物件，避免下游拿到 undefined
      const data = response.data || response || {}
      withdrawData.value = data
      store.setWithdrawRule(data)
      store.setPending(true)
    } catch (error) {
      console.error('[useReward] fetchWithdrawRulePreview error:', error)
    } finally {
      setLoading({ inbox: false })
    }
  }

  const back = () => {
    store.resetPendingState()
  }

  // 領獎後開啟 Inbox 的唯一入口。一律帶上 tabType，Inbox 才不會退回未讀優先序 —— 規格要求
  // 領獎路徑只看獎勵類別、不看未讀狀態。未列舉的類別（含一次領到多種）都回 REWARD。
  const openInboxForRewardType = async (type) => {
    setModal(null)
    await nextTick()
    const tabType = INBOX_TAB_TYPE_BY_REWARD_TYPE[type] || 'REWARD'
    setModalParams({ tabType })
    setModal('ModalInbox')
  }

  // 退路而非主線：身分一變清單就被清空，這裡就查不到了（規格 §5.5）。
  // 元件已把整列捕獲成本地狀態時應直接傳 ownerKey
  const findRowOwnerKey = (targetReceiveNo) => {
    if (!targetReceiveNo) return null
    const row = [...rewardList.value, ...otherRewardList.value, ...pointsRewardList.value].find(
      (item) => String(item?.receiveNo) === String(targetReceiveNo)
    )
    return row?.ownerKey || null
  }

  // 必須寫在 $request.send 之前而非元件點擊入口：中間隔著 verify() 的 OTP＋綁卡，人時等待無上限。
  // 基準取當下 token 而非 userInfo —— userInfo 要等 /self 才換，事故當下正是舊的（規格 §5.5）
  const assertRewardOwnership = ({ receiveNo: targetReceiveNo, ownerKey }) => {
    const currentKey = identityKey(token.value)
    const rowOwnerKey = ownerKey || findRowOwnerKey(targetReceiveNo)

    if (isRewardClaimAllowed({ currentKey, rowOwnerKey, listOwnerKey: store.rewardListOwnerKey })) {
      return
    }

    nuxtApp.$sentry?.captureMessage('[reward] Owner mismatch, claim blocked', {
      level: 'warning',
      extra: {
        receiveNo: targetReceiveNo,
        currentIdentityKey: currentKey,
        rowOwnerKey,
        listOwnerKey: store.rewardListOwnerKey,
      },
    })

    // 殘留已被證實：清掉前一個身分的獎勵清單並重抓，讓畫面回到當前帳號
    store.resetUserScoped()
    fetchRewardList().catch(() => {})

    // 不換 modal：Reward.vue 活在 ModalInbox 內，setModal 會把收件匣關掉。
    // $i18n 在 plugin 階段建立本 composable 時可能尚未就緒（見上方 lang 初始化），故同樣防護
    const blockedMessage = $i18n?.t('description.please_try_again_later')
    if (blockedMessage) nuxtApp.$toast?.error(blockedMessage)

    const mismatchError = new Error('REWARD_OWNER_MISMATCH')
    // 沿用 request 層「已呈現、勿重複上報」的契約（01.request.js）：上方已 captureMessage，
    // 呼叫端據此吞掉 rejection，避免同一次阻擋在 Sentry 出現兩筆
    mismatchError.isBusinessError = true
    throw mismatchError
  }

  // FREE_ 規則的獎勵會換掉提款規則，館別清單跟著變
  const resetToDefaultRoomAfterFreeReward = async (originalModal) => {
    if (!import.meta.client) return
    await new Promise((resolve) => setTimeout(resolve, FREE_REWARD_RETURN_DELAY_MS))
    await resetToDefaultRoom({ originalModal })
  }

  const receiveReward = async ({
    activityNo: newActivityNo,
    receiveNo: newReceiveNo,
    ownerKey,
    attended,
    callback: rewardCallback,
    rewardType: newRewardType,
    withdrawRule: rewardWithdrawRule = '',
  }) => {
    const originalModal = mainStore.modal
    let data
    store.setReceiving(true)
    store.setCallback(rewardCallback || null)
    store.setRewardType(newRewardType || null)

    try {
      const validateReceiveNo = Number(newReceiveNo) > 0 ? Number(newReceiveNo) : undefined

      // 重置 pending 狀態
      store.setPending(false)
      setLoading({ inbox: true })

      assertRewardOwnership({ receiveNo: validateReceiveNo, ownerKey })

      const response = await $request.send(
        activityApi.getReward({
          activityNo: newActivityNo,
          receiveNo: validateReceiveNo,
          appSecret,
          attended,
        })
      )

      data = response.data || response

      // 刷新獎勵列表
      await fetchRewardList()

      // 如果不是 PRIZE 類型，執行回調
      if (newRewardType !== 'PRIZE' && rewardCallback) {
        rewardCallback(data)
      }
    } catch (error) {
      store.setPending(false)
      console.error('[useReward] receiveReward error:', error)
      throw error
    } finally {
      store.setReceiving(false)
      setLoading({ inbox: false })
    }

    // 領取已成功，回首頁屬附帶動作，不阻塞也不讓其失敗改變領取結果
    if (rewardWithdrawRule.includes('FREE_')) {
      resetToDefaultRoomAfterFreeReward(originalModal).catch((error) => {
        if (!isRequestErrorAlreadySurfaced(error)) throw error
      })
    }
    return data
  }

  const receiveAllPoints = async (callbackOrOptions) => {
    store.setReceiving(true)

    // Support both old signature (callback) and new signature ({ callback })
    const rewardCallback = typeof callbackOrOptions === 'function' ? callbackOrOptions : callbackOrOptions?.callback

    store.setCallback(rewardCallback || null)

    try {
      // 一鍵領取沒有單列 receiveNo，以清單層級的章比對；未蓋章時仍 fail-open。
      // 這條路徑同樣隔著 verify('GET_REWARD') 的 OTP／綁卡等待，身分來得及被換掉（TI-15612）
      assertRewardOwnership({})

      const response = await $request.send(
        activityApi.collect({
          collectType: 'collectPoints',
          appSecret,
        })
      )

      const data = response.data || response

      // 刷新獎勵列表
      await fetchRewardList()

      // 執行回調
      if (rewardCallback) {
        rewardCallback(data)
      }

      return data
    } catch (error) {
      console.error('[useReward] receiveAllPoints error:', error)
      throw error
    } finally {
      store.setReceiving(false)
    }
  }

  const setIsPlaying = (newIsPlaying) => {
    store.setIsPlaying(newIsPlaying)
  }

  const setLang = (locale) => {
    store.setLang(locale || lang.value || 'en-US')
  }

  const refresh = async () => {
    await fetchRewardList()
  }

  const startAutoRefresh = () => {
    if (!import.meta.client) return

    stopAutoRefresh()

    ctx.refreshTimer = setInterval(() => {
      // 如果不在遊戲中，才自動刷新
      if (!isPlaying.value) {
        // 背景輪詢用：吞掉 rejection，避免 unhandledrejection 上報 Sentry
        // fetchRewardList 內已 console.error；真實錯誤已於 request 層上報，失敗等下一輪（20s）自動重試
        fetchRewardList().catch(() => {})
      }
    }, 20000)
  }

  const stopAutoRefresh = () => {
    if (!import.meta.client) return

    if (ctx.refreshTimer) {
      clearInterval(ctx.refreshTimer)
      ctx.refreshTimer = null
    }
  }

  // 元件卸載時清理 timer，避免記憶體洩漏
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      stopAutoRefresh()
    })
  }

  return {
    otherRewardList,
    pointsRewardList,
    rewardList,
    activityNo,
    receiveNo,
    withdrawRule,
    rewardType,
    isPlaying,
    callback,
    orderColumn,
    lang,
    receiving,
    pending,
    getPending,
    withdrawData,
    fetchRewardList,
    fetchOtherRewardList,
    fetchWithdrawRulePreview,
    receiveReward,
    receiveAllPoints,
    setIsPlaying,
    setLang,
    refresh,
    back,
    openInboxForRewardType,
    startAutoRefresh,
    stopAutoRefresh,
  }
}
